import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { compactSelectClass } from '../styles';
import { fetchOrgBalances } from './api';
import type { OrdersMeta, OrgBalanceRow } from './types';

interface Props {
  meta: OrdersMeta;
}

const OrgBalancesPanel: React.FC<Props> = ({ meta }) => {
  const [rows, setRows] = useState<OrgBalanceRow[]>([]);
  const [orgFilter, setOrgFilter] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchOrgBalances({ org: orgFilter || undefined });
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load balances');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [orgFilter]);

  return (
    <div className="space-y-3">
      <select
        className={compactSelectClass}
        value={orgFilter}
        onChange={(e) => setOrgFilter(e.target.value ? Number(e.target.value) : '')}
        aria-label="Filter by organization"
      >
        <option value="">All orgs</option>
        {meta.orgs.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
      {error && <div className="rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#8A8A8A] py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#E8E8E8] rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[12px] text-[#626262]">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Organization</th>
                <th className="text-right px-3 py-2 font-medium">Marketplace credits</th>
                <th className="text-right px-3 py-2 font-medium">Authoritative total</th>
                <th className="text-right px-3 py-2 font-medium">Blueprint tokens</th>
                <th className="text-left px-3 py-2 font-medium">Updated</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.org} className="border-t border-[#F0F0F0]">
                  <td className="px-3 py-2">{row.org_name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {row.is_unlimited ? 'Unlimited' : row.credits_marketplace ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {row.is_unlimited ? 'Unlimited' : row.authoritative_total ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {row.tokens_blueprint ?? '—'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {row.updated_at ? new Date(row.updated_at).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-[#8A8A8A]">
                    No balances
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OrgBalancesPanel;
